import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatPrice } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PaymentSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const listingId = params.get("listing_id");
    const [state, setState] = useState({ status: "checking", payment_status: null, amount: null });
    const attemptsRef = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", payment_status: "missing", amount: null });
            return undefined;
        }

        let cancelled = false;
        const MAX_ATTEMPTS = 10;
        const INTERVAL_MS = 2000;

        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (cancelled) return;
                attemptsRef.current += 1;
                if (data.payment_status === "paid") {
                    setState({ status: "paid", payment_status: "paid", amount: data.amount_total, currency: data.currency });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired", payment_status: data.payment_status, amount: data.amount_total });
                    return;
                }
                if (attemptsRef.current >= MAX_ATTEMPTS) {
                    setState({ status: "timeout", payment_status: data.payment_status, amount: data.amount_total });
                    return;
                }
                setState({ status: "pending", payment_status: data.payment_status, amount: data.amount_total });
                setTimeout(poll, INTERVAL_MS);
            } catch (e) {
                if (!cancelled) setState({ status: "error", payment_status: null, amount: null });
            }
        };

        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    const isPaid = state.status === "paid";
    const isPending = state.status === "checking" || state.status === "pending";
    const isError = state.status === "error" || state.status === "expired" || state.status === "timeout";

    return (
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12" data-testid="payment-success-page">
            <div className="w-full max-w-md bg-white border border-gray-200 rounded-sm p-8 text-center">
                {isPending && (
                    <>
                        <Loader2 className="w-16 h-16 text-[#FFCC00] mx-auto mb-4 animate-spin" />
                        <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight mb-2">Zahlung wird geprüft...</h1>
                        <p className="text-gray-600 text-sm mb-2">Bitte einen Moment Geduld — wir bestätigen die Zahlung mit Stripe.</p>
                        <p className="text-xs text-gray-400">Dies kann einige Sekunden dauern. Sie können diese Seite geöffnet lassen.</p>
                    </>
                )}
                {isPaid && (
                    <>
                        <div className="w-16 h-16 bg-green-100 mx-auto mb-4 rounded-sm flex items-center justify-center">
                            <CheckCircle2 className="w-10 h-10 text-green-600" />
                        </div>
                        <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight mb-2">Zahlung erfolgreich</h1>
                        <p className="text-gray-600 text-sm mb-6">Vielen Dank! Ihre Zahlung wurde verbucht. Der Verkäufer wird sich in Kürze bei Ihnen melden.</p>
                        {state.amount && (
                            <div className="bg-gray-50 border border-gray-200 rounded-sm p-4 mb-6">
                                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-1">Bezahlter Betrag</div>
                                <div className="font-display font-black text-3xl font-mono-num">{formatPrice((state.amount || 0) / 100)}</div>
                            </div>
                        )}
                        <div className="flex flex-col gap-2">
                            <Link to="/dashboard?tab=messages">
                                <Button className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="goto-messages">
                                    Nachrichten öffnen <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                            {listingId && (
                                <Link to={`/listing/${listingId}`} target="_blank" rel="noopener noreferrer">
                                    <Button variant="outline" className="w-full rounded-sm border-[#111827]">Inserat ansehen</Button>
                                </Link>
                            )}
                        </div>
                    </>
                )}
                {isError && (
                    <>
                        <div className="w-16 h-16 bg-red-100 mx-auto mb-4 rounded-sm flex items-center justify-center">
                            <XCircle className="w-10 h-10 text-red-600" />
                        </div>
                        <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight mb-2">
                            {state.status === "expired" ? "Sitzung abgelaufen" : state.status === "timeout" ? "Zeitüberschreitung" : "Etwas ist schiefgelaufen"}
                        </h1>
                        <p className="text-gray-600 text-sm mb-6">
                            {state.status === "expired"
                                ? "Die Zahlungssitzung ist abgelaufen. Bitte starten Sie den Vorgang erneut."
                                : state.status === "timeout"
                                ? "Die Zahlung wird noch verarbeitet. Bitte prüfen Sie Ihr Dashboard in wenigen Minuten."
                                : "Wir konnten den Zahlungsstatus nicht abrufen."}
                        </p>
                        <Link to="/dashboard">
                            <Button className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm">Zum Dashboard</Button>
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
